import React, { useState } from 'react';
import { Button, Card, Descriptions, Input, Modal, Popconfirm, Space, Table, Tag, Upload, message } from 'antd';
import type { UploadProps } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { showError } from 'utils/exceptionUtils';
import { fileUpload } from '../../utils/storageUtils';
import type { DspBillPaymentInfoType, DspBillPaymentVoucherType } from '../../graphql/queries/settlement/dspBillPayment';
import {
    useAddDspBillPaymentVoucher,
    useConfirmDspBillInvoice,
    useConfirmDspBillPayment,
    useDeleteDspBillPaymentVoucher,
    useRejectDspBillInvoice,
    useRevokeDspBillInvoiceConfirm
} from '../../graphql/mutations/settlement/dspBillPayment';

/** 与后端 settlement_bill.reject_reason 列长一致 */
const REJECT_REASON_MAX_LENGTH = 500;

/** 与后端 DspBillPaymentVoucherConstants 保持一致 */
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf', 'doc', 'docx'];
const STORAGE_USAGE = 'dsp-payment-voucher';

interface DspBillPaymentInfoCardProps {
    billId: string;
    billStatus?: string | null;
    paymentInfo?: DspBillPaymentInfoType | null;
    loading?: boolean;
    /** 是否具备总部财务操作权限（付款凭证上传 / 删除）；HUB 与 DSP Owner 恒为 false（纯只读） */
    canOperate?: boolean;
    /** 是否可确认 / 驳回 / 撤销确认 Invoice；HUB 与 DSP Owner 恒为 false */
    canConfirmInvoice?: boolean;
    /** 是否可确认付款；HUB 与 DSP Owner 恒为 false */
    canPay?: boolean;
    /**
     * 账期是否在 DSP Invoice 线上化切换点之后。false 时隐藏全部操作按钮 ——
     * 该账单走线下付款，永久停在 EXTERNAL_CONFIRMED 或 INVOICE_CONFIRMED。
     * 付款信息与付款凭证本身不受影响，仍对财务可见。
     */
    invoiceEligible?: boolean;
    /**
     * DSP Owner 侧口径：不展示预计付款日期（账期天数属 SwiftX 侧商务条款，服务端亦不下发），
     * 账单付款时间仅在账单已付款后展示。总部与 HUB 侧保持恒展示、为空显示 '-'，
     * 以便与「DSP 不存在导致预计付款日为空」的数据缺失场景区分开。
     */
    dspOwnerView?: boolean;
    onRefresh?: () => void;
    /** 账单状态发生流转后回调，供页面刷新账单主体 */
    onStatusChanged?: () => void;
}

const formatTimestamp = (seconds?: number | null): string => {
    if (!seconds) return '-';
    return new Date(seconds * 1000).toLocaleString();
};

const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

/**
 * DSP 账单「付款信息」区块，总部 / HUB / DSP Owner 三端共用。
 *
 * - 敏感字段（税号、银行账号）由服务端按权限决定是否掩码，前端直接展示所得值
 * - Invoice 确认类按钮由 canConfirmInvoice 控制，确认付款由 canPay 控制，凭证操作由 canOperate 控制（总部财务）
 * - 未上传凭证且无操作权限时，整个凭证区域不展示（PRD 要求）
 */
const DspBillPaymentInfoCard: React.FC<DspBillPaymentInfoCardProps> = ({
    billId,
    billStatus,
    paymentInfo,
    loading = false,
    canOperate = false,
    canConfirmInvoice = false,
    canPay = false,
    invoiceEligible = false,
    dspOwnerView = false,
    onRefresh,
    onStatusChanged
}) => {
    const { t } = useTranslation();
    const [uploading, setUploading] = useState(false);

    const [confirmInvoice] = useConfirmDspBillInvoice();
    const [revokeInvoiceConfirm] = useRevokeDspBillInvoiceConfirm();
    const [confirmPayment] = useConfirmDspBillPayment();
    const [rejectInvoice, { loading: rejecting }] = useRejectDspBillInvoice();
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [addVoucher] = useAddDspBillPaymentVoucher();
    const [deleteVoucher] = useDeleteDspBillPaymentVoucher();

    const financeInfo = paymentInfo?.financeInfo;
    const vouchers = paymentInfo?.vouchers ?? [];
    // DSP 未提交 Invoice 前不展示「确认 Invoice」：服务端 confirmDspBillInvoice 必然拒绝，
    // 展示出来只是给财务一个必然报错的按钮
    const invoiceSubmitted = paymentInfo?.invoiceSubmitted === true;
    // 自制件的票号与金额均由 DSP 自填，非系统按模板生成，确认前须提示财务已核对关键信息。
    // 服务端已保证该字段带 submitted 门控（未提交的自制草稿不下发，见 D10-6）
    const invoiceSelfMade = paymentInfo?.invoiceSelfMade === true;

    const runStatusChange = async (
        action: () => Promise<{ success: boolean; errorMessage?: string | null } | undefined>,
        confirmContent: string
    ) => {
        Modal.confirm({
            title: t('dspBillPayment.title'),
            content: confirmContent,
            onOk: async () => {
                try {
                    const result = await action();
                    if (!result?.success) {
                        message.error(result?.errorMessage || t('dspBillPayment.messages.operateSuccess'));
                        return;
                    }
                    message.success(t('dspBillPayment.messages.operateSuccess'));
                    onRefresh?.();
                    onStatusChanged?.();
                } catch (e) {
                    showError(e);
                }
            }
        });
    };

    const handleConfirmInvoice = () =>
        runStatusChange(
            async () => (await confirmInvoice({ variables: { billId } })).data?.confirmDspBillInvoice,
            invoiceSelfMade
                ? t('dspBillPayment.confirm.confirmInvoiceSelfMade')
                : t('dspBillPayment.confirm.confirmInvoice')
        );

    const handleRevokeInvoiceConfirm = () =>
        runStatusChange(
            async () => (await revokeInvoiceConfirm({ variables: { billId } })).data?.revokeDspBillInvoiceConfirm,
            t('dspBillPayment.confirm.revokeInvoiceConfirm')
        );

    const handleConfirmPayment = () =>
        runStatusChange(
            async () => (await confirmPayment({ variables: { billId } })).data?.confirmDspBillPayment,
            t('dspBillPayment.confirm.confirmPayment')
        );

    const openRejectModal = () => {
        setRejectReason('');
        setRejectOpen(true);
    };

    const handleRejectInvoice = async () => {
        try {
            const result = (await rejectInvoice({ variables: { billId, reason: rejectReason.trim() } })).data
                ?.rejectDspBillInvoice;
            if (!result?.success) {
                message.error(result?.errorMessage || '');
                return;
            }
            message.success(t('dspBillPayment.messages.operateSuccess'));
            setRejectOpen(false);
            onRefresh?.();
            onStatusChanged?.();
        } catch (e) {
            showError(e);
        }
    };

    // 前端做一次快速拦截，真正的白名单校验在服务端
    const beforeUpload: UploadProps['beforeUpload'] = async file => {
        const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
        if (!ALLOWED_EXTENSIONS.includes(extension)) {
            message.error(t('dspBillPayment.messages.fileTypeNotAllowed') as string);
            return Upload.LIST_IGNORE;
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
            message.error(t('dspBillPayment.messages.fileTooLarge') as string);
            return Upload.LIST_IGNORE;
        }

        setUploading(true);
        try {
            const { objectKey } = await fileUpload(file, STORAGE_USAGE);
            await addVoucher({
                variables: {
                    billId,
                    fileObjectKey: objectKey,
                    fileName: file.name,
                    fileSize: file.size,
                    contentType: file.type
                }
            });
            message.success(t('dspBillPayment.messages.uploadSuccess') as string);
            onRefresh?.();
        } catch (e) {
            showError(e);
        } finally {
            setUploading(false);
        }
        // 走自定义上传流程，阻止 antd 默认上传
        return Upload.LIST_IGNORE;
    };

    const handleDeleteVoucher = async (voucherId: string) => {
        try {
            const { data } = await deleteVoucher({ variables: { billId, voucherId } });
            if (!data?.deleteDspBillPaymentVoucher?.success) {
                message.error(data?.deleteDspBillPaymentVoucher?.errorMessage || '');
                return;
            }
            message.success(t('dspBillPayment.messages.deleteSuccess') as string);
            onRefresh?.();
        } catch (e) {
            showError(e);
        }
    };

    const voucherColumns = [
        {
            title: t('dspBillPayment.fileName'),
            dataIndex: 'fileName',
            key: 'fileName',
            render: (fileName: string, record: DspBillPaymentVoucherType) =>
                record.downloadUrl ? (
                    <a href={record.downloadUrl} target="_blank" rel="noreferrer">
                        {fileName}
                    </a>
                ) : (
                    fileName
                )
        },
        {
            title: t('table.size', { defaultValue: 'Size' }),
            dataIndex: 'fileSize',
            key: 'fileSize',
            render: (size: number) => formatFileSize(size)
        },
        {
            title: t('dspBillPayment.uploadedBy'),
            dataIndex: 'uploadedByName',
            key: 'uploadedByName',
            render: (name?: string | null) => name || '-'
        },
        {
            title: t('dspBillPayment.uploadedAt'),
            dataIndex: 'uploadedAt',
            key: 'uploadedAt',
            render: (uploadedAt?: number | null) => formatTimestamp(uploadedAt)
        },
        ...(canOperate
            ? [
                  {
                      title: '',
                      key: 'action',
                      render: (_: unknown, record: DspBillPaymentVoucherType) => (
                          <Popconfirm
                              title={t('dspBillPayment.confirm.deleteVoucher')}
                              onConfirm={() => handleDeleteVoucher(record.id)}
                          >
                              <Button type="link" danger>
                                  {t('components.button.delete')}
                              </Button>
                          </Popconfirm>
                      )
                  }
              ]
            : [])
    ];

    // 付款凭证是「已付款」这件事的证据，账单未到 PAID 时不展示凭证区块（含已上传的凭证列表）。
    // 内层的 vouchers.length > 0 || canOperate 保留原有规则：未上传凭证且无操作权限时整块不展示。
    const showVoucherSection = billStatus === 'PAID' && (vouchers.length > 0 || canOperate);
    // DSP Owner 侧只在账单已付款后展示付款时间；总部与 HUB 侧恒展示，为空显示 '-'
    const showPaidAt = !dspOwnerView || billStatus === 'PAID';

    return (
        <Card
            title={t('dspBillPayment.title')}
            className="mb-4"
            loading={loading}
            extra={
                (canConfirmInvoice || canPay) && invoiceEligible ? (
                    <Space>
                        {canConfirmInvoice && billStatus === 'EXTERNAL_CONFIRMED' && invoiceSubmitted && (
                            <>
                                <Button danger onClick={openRejectModal}>
                                    {t('dspBillPayment.actions.rejectInvoice')}
                                </Button>
                                <Button type="primary" onClick={handleConfirmInvoice}>
                                    {t('dspBillPayment.actions.confirmInvoice')}
                                </Button>
                            </>
                        )}
                        {billStatus === 'INVOICE_CONFIRMED' && (
                            <>
                                {canConfirmInvoice && (
                                    <Button onClick={handleRevokeInvoiceConfirm}>
                                        {t('dspBillPayment.actions.revokeInvoiceConfirm')}
                                    </Button>
                                )}
                                {canPay && (
                                    <Button type="primary" onClick={handleConfirmPayment}>
                                        {t('dspBillPayment.actions.confirmPayment')}
                                    </Button>
                                )}
                            </>
                        )}
                    </Space>
                ) : null
            }
        >
            <Descriptions column={2} size="small">
                <Descriptions.Item label={t('dspFinanceInfo.fields.vendorCountry')}>
                    {financeInfo?.vendorCountry || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('dspFinanceInfo.fields.federalTaxClassification')}>
                    {financeInfo?.federalTaxClassification
                        ? t(`dspFinanceInfo.federalTaxClassification.${financeInfo.federalTaxClassification}`)
                        : '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('dspFinanceInfo.fields.taxIdType')}>
                    {financeInfo?.taxIdType ? t(`dspFinanceInfo.taxIdType.${financeInfo.taxIdType}`) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('dspFinanceInfo.fields.taxIdNumber')}>
                    {financeInfo?.taxIdNumber || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('dspFinanceInfo.fields.trackFor1099')}>
                    {financeInfo?.trackFor1099 == null
                        ? '-'
                        : financeInfo.trackFor1099
                          ? t('dspFinanceInfo.options.yes')
                          : t('dspFinanceInfo.options.no')}
                </Descriptions.Item>
                <Descriptions.Item label={t('dspFinanceInfo.fields.bankName')}>
                    {financeInfo?.bankName || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('dspFinanceInfo.fields.accountType')}>
                    {financeInfo?.accountType ? t(`dspFinanceInfo.accountType.${financeInfo.accountType}`) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('dspFinanceInfo.fields.accountNo')}>
                    {financeInfo?.accountNo || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('dspFinanceInfo.fields.routingNo')}>
                    {financeInfo?.routingNo || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('dspFinanceInfo.fields.vendorAddress')} span={2}>
                    {financeInfo?.vendorAddress || '-'}
                </Descriptions.Item>
                {/* 预计付款日期 = 出账时间 + 账期天数，由服务端统一计算；DSP Owner 侧不展示 */}
                {!dspOwnerView && (
                    <Descriptions.Item label={t('dspBillPayment.expectedPaymentDate')}>
                        {formatTimestamp(paymentInfo?.expectedPaymentDate)}
                    </Descriptions.Item>
                )}
                {showPaidAt && (
                    <Descriptions.Item label={t('dspBillPayment.paidAt')}>
                        {paymentInfo?.paidAt ? (
                            <Tag color="success">{formatTimestamp(paymentInfo.paidAt)}</Tag>
                        ) : (
                            '-'
                        )}
                    </Descriptions.Item>
                )}
            </Descriptions>

            {showVoucherSection && (
                <div className="mt-4">
                    <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold">{t('dspBillPayment.voucher')}</span>
                        {canOperate && billStatus === 'PAID' && (
                            <Upload beforeUpload={beforeUpload} showUploadList={false} maxCount={1}>
                                <Button icon={<UploadOutlined />} loading={uploading}>
                                    {t('dspBillPayment.uploadVoucher')}
                                </Button>
                            </Upload>
                        )}
                    </div>
                    <Table
                        rowKey="id"
                        size="small"
                        columns={voucherColumns}
                        dataSource={vouchers}
                        pagination={false}
                    />
                </div>
            )}
            <Modal
                open={rejectOpen}
                title={t('dspBillPayment.reject.title')}
                okText={t('dspBillPayment.actions.rejectInvoice')}
                okButtonProps={{ danger: true, disabled: !rejectReason.trim(), loading: rejecting }}
                onOk={handleRejectInvoice}
                onCancel={() => setRejectOpen(false)}
                destroyOnClose
            >
                <div className="mb-2">{t('dspBillPayment.reject.reasonLabel')}</div>
                <Input.TextArea
                    rows={4}
                    maxLength={REJECT_REASON_MAX_LENGTH}
                    showCount
                    value={rejectReason}
                    placeholder={t('dspBillPayment.reject.reasonPlaceholder') as string}
                    onChange={e => setRejectReason(e.target.value)}
                />
            </Modal>
        </Card>
    );
};

export default DspBillPaymentInfoCard;
